import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { GasStatusBadge } from "./TechnicalStatusBadge";
import { Flame, Plus, Pencil, AlertTriangle, FileText, Upload, Trash2, CheckCircle2 } from "lucide-react";

function DaysChip({ dateStr }) {
  if (!dateStr) return <span className="text-xs text-gray-400">—</span>;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (diff < 0)   return <span className="text-xs font-semibold text-red-600">Caducada</span>;
  if (diff <= 30) return <span className="text-xs font-semibold text-orange-500">En {diff}d</span>;
  if (diff <= 90) return <span className="text-xs font-semibold text-yellow-600">En {diff}d</span>;
  return <span className="text-xs text-green-600">En {diff}d</span>;
}

function GasForm({ item, venueId, onSave, onClose }) {
  const empty = {
    venue_id: venueId, name: "", installation_type: "gas_natural",
    pressure_type: "baixa_pressio", location: "",
    installer_company: "", installer_license: "",
    maintenance_company: "", maintenance_company_phone: "", maintenance_company_email: "",
    installation_date: "", last_inspection_date: "", next_inspection_date: "",
    inspection_frequency_years: 5, certificate_url: "", bulletin_url: "",
    incidents: [], status: "ok", notes: "", active: true,
  };
  const [form, setForm] = useState(item || empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [showIncident, setShowIncident] = useState(false);
  const [newIncident, setNewIncident] = useState({ date: "", description: "", corrective_action: "", resolved: false });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadFile = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set(field, file_url);
    setUploading(null);
  };

  const addIncident = () => {
    if (!newIncident.description) return;
    set("incidents", [...(form.incidents || []), { ...newIncident, date: newIncident.date || new Date().toISOString().slice(0, 10) }]);
    setNewIncident({ date: "", description: "", corrective_action: "", resolved: false });
    setShowIncident(false);
  };

  const toggleResolve = (idx) => {
    const inc = [...(form.incidents || [])];
    inc[idx] = { ...inc[idx], resolved: !inc[idx].resolved };
    set("incidents", inc);
  };

  const handleSave = async () => {
    setSaving(true);
    if (item?.id) {
      await hosys.entities.GasInstallation.update(item.id, form);
    } else {
      await hosys.entities.GasInstallation.create(form);
    }
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar instal·lació de gas" : "Nova instal·lació de gas"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nom *</Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Xarxa gas cuina" />
            </div>
            <div>
              <Label>Tipus instal·lació</Label>
              <Select value={form.installation_type} onValueChange={v => set("installation_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gas_natural">Gas natural</SelectItem>
                  <SelectItem value="gas_propà">Gas propà</SelectItem>
                  <SelectItem value="gas_butà">Gas butà</SelectItem>
                  <SelectItem value="gasoil">Gasoil</SelectItem>
                  <SelectItem value="mixt">Mixt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pressió xarxa</Label>
              <Select value={form.pressure_type} onValueChange={v => set("pressure_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa_pressio">Baixa pressió</SelectItem>
                  <SelectItem value="mitja_pressio">Mitja pressió</SelectItem>
                  <SelectItem value="alta_pressio">Alta pressió</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ubicació</Label>
              <Input value={form.location} onChange={e => set("location", e.target.value)} />
            </div>
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ok">Correcte</SelectItem>
                  <SelectItem value="pending_inspection">Inspecció pendent</SelectItem>
                  <SelectItem value="expired">Caducada</SelectItem>
                  <SelectItem value="incident_open">Incidència oberta</SelectItem>
                  <SelectItem value="inactive">Inactiu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data instal·lació</Label>
              <Input type="date" value={form.installation_date} onChange={e => set("installation_date", e.target.value)} />
            </div>
            <div>
              <Label>Última inspecció</Label>
              <Input type="date" value={form.last_inspection_date} onChange={e => set("last_inspection_date", e.target.value)} />
            </div>
            <div>
              <Label>Propera inspecció</Label>
              <Input type="date" value={form.next_inspection_date} onChange={e => set("next_inspection_date", e.target.value)} />
            </div>
            <div>
              <Label>Periodicitat (anys)</Label>
              <Input type="number" value={form.inspection_frequency_years} onChange={e => set("inspection_frequency_years", Number(e.target.value))} />
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Empresa instal·ladora</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Empresa</Label>
                <Input value={form.installer_company} onChange={e => set("installer_company", e.target.value)} />
              </div>
              <div>
                <Label>Llicència</Label>
                <Input value={form.installer_license} onChange={e => set("installer_license", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Empresa mantenidora</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Empresa</Label>
                <Input value={form.maintenance_company} onChange={e => set("maintenance_company", e.target.value)} />
              </div>
              <div>
                <Label>Telèfon</Label>
                <Input value={form.maintenance_company_phone} onChange={e => set("maintenance_company_phone", e.target.value)} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.maintenance_company_email} onChange={e => set("maintenance_company_email", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documents</p>
            <div className="grid grid-cols-2 gap-3">
              {[["certificate_url", "Certificat instal·lació"], ["bulletin_url", "Butlletí de gas"]].map(([field, label]) => (
                <div key={field}>
                  <Label>{label}</Label>
                  {form[field] ? (
                    <div className="flex items-center gap-1 mt-1">
                      <a href={form[field]} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">Veure doc.</a>
                      <button onClick={() => set(field, "")} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <label className="mt-1 flex items-center gap-1.5 cursor-pointer text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg px-2 py-1.5">
                      {uploading === field ? "Pujant..." : <><Upload className="w-3 h-3" /> Pujar</>}
                      <input type="file" className="hidden" accept=".pdf,.jpg,.png" onChange={e => uploadFile(e, field)} />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Incidències</p>
              <Button size="sm" variant="outline" onClick={() => setShowIncident(!showIncident)} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" /> Afegir
              </Button>
            </div>
            {showIncident && (
              <div className="bg-gray-50 rounded-xl p-3 mb-2 grid gap-2">
                <Input type="date" value={newIncident.date} onChange={e => setNewIncident(i => ({ ...i, date: e.target.value }))} placeholder="Data" />
                <Textarea value={newIncident.description} onChange={e => setNewIncident(i => ({ ...i, description: e.target.value }))} placeholder="Descripció de la incidència" rows={2} />
                <Textarea value={newIncident.corrective_action} onChange={e => setNewIncident(i => ({ ...i, corrective_action: e.target.value }))} placeholder="Acció correctiva" rows={2} />
                <Button size="sm" onClick={addIncident} className="bg-[#7B2D3F] text-white">Afegir incidència</Button>
              </div>
            )}
            {(form.incidents || []).map((inc, idx) => (
              <div key={`inc-${idx}`} className={`flex items-start gap-2 p-2 rounded-lg text-xs mb-1 ${inc.resolved ? "bg-green-50" : "bg-orange-50"}`}>
                <button onClick={() => toggleResolve(idx)} className="mt-0.5 flex-shrink-0">
                  {inc.resolved ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-orange-500" />}
                </button>
                <div>
                  <p className="text-gray-700 font-medium">{inc.description}</p>
                  {inc.corrective_action && <p className="text-gray-500 mt-0.5">→ {inc.corrective_action}</p>}
                  {inc.date && <p className="text-gray-400 mt-0.5">{inc.date}</p>}
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
            {saving ? "Guardant..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GasInstallationTab({ items, onRefresh, venueId }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{items.length} instal·lació{items.length !== 1 ? "ns" : ""} registrada{items.length !== 1 ? "des" : ""}</p>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-1.5" size="sm">
          <Plus className="w-4 h-4" /> Nova instal·lació
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-14">
          <Flame className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Cap instal·lació de gas registrada</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map(inst => {
            const openIncidents = (inst.incidents || []).filter(i => !i.resolved);
            return (
              <div key={inst.id} className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{inst.name}</span>
                      <GasStatusBadge status={inst.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {inst.installation_type?.replace(/_/g, " ")}
                      {inst.location && ` · ${inst.location}`}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(inst); setShowForm(true); }} className="h-7 w-7 p-0">
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs mb-2">
                  <div>
                    <span className="text-gray-400">Última inspecció: </span>
                    <span className="text-gray-700">{inst.last_inspection_date ? new Date(inst.last_inspection_date).toLocaleDateString("ca-ES") : "—"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400">Pròxima: </span>
                    <span className="text-gray-700">{inst.next_inspection_date ? new Date(inst.next_inspection_date).toLocaleDateString("ca-ES") : "—"}</span>
                    <DaysChip dateStr={inst.next_inspection_date} />
                  </div>
                </div>
                {openIncidents.length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs bg-orange-50 text-orange-700 rounded-lg px-2 py-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {openIncidents.length} incidència{openIncidents.length > 1 ? "s" : ""} oberta{openIncidents.length > 1 ? "s" : ""}
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  {inst.certificate_url && <a href={inst.certificate_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"><FileText className="w-3 h-3" />Certificat</a>}
                  {inst.bulletin_url && <a href={inst.bulletin_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"><FileText className="w-3 h-3" />Butlletí</a>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <GasForm
          item={editing}
          venueId={venueId}
          onSave={() => { setShowForm(false); setEditing(null); onRefresh(); }}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}